export interface Problem {
  id: string;
  statement: string;
  query: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  hint: string;
}

export interface Subject {
  id: string;
  name: string;
  icon: string;
  color: string;
  problemCount: number;
  problems: Problem[];
}

export const learningSubjects: Subject[] = [
  {
    id: 'algebra',
    name: 'Algebra',
    icon: 'ri-function-line',
    color: 'from-emerald-500 to-teal-600',
    problemCount: 12,
    problems: [
      {
        id: 'alg-1',
        statement: 'Solve the linear equation: 2x + 5 = 13',
        query: 'solve 2x + 5 = 13',
        difficulty: 'Beginner',
        hint: 'Isolate x by subtracting 5 from both sides, then divide by 2'
      },
      {
        id: 'alg-2',
        statement: 'Factor the quadratic expression: x² + 5x + 6',
        query: 'factor x^2 + 5x + 6',
        difficulty: 'Beginner',
        hint: 'Look for two numbers that multiply to 6 and add to 5'
      },
      {
        id: 'alg-3',
        statement: 'Solve the quadratic equation: x² - 7x + 12 = 0',
        query: 'solve x^2 - 7x + 12 = 0',
        difficulty: 'Beginner',
        hint: 'Try factoring or use the quadratic formula'
      },
      {
        id: 'alg-4',
        statement: 'Simplify the expression: (3x² + 2x - 5) - (x² - 4x + 3)',
        query: 'simplify (3x^2 + 2x - 5) - (x^2 - 4x + 3)',
        difficulty: 'Intermediate',
        hint: 'Distribute the negative sign and combine like terms'
      },
      {
        id: 'alg-5',
        statement: 'Solve the system of equations: 2x + y = 7 and x - y = 2',
        query: 'solve 2x + y = 7, x - y = 2',
        difficulty: 'Intermediate',
        hint: 'Try the elimination method by adding the equations'
      },
      {
        id: 'alg-6',
        statement: 'Find the vertex of the parabola: y = 2x² - 8x + 5',
        query: 'vertex of y = 2x^2 - 8x + 5',
        difficulty: 'Intermediate',
        hint: 'Use the formula x = -b/(2a) to find the x-coordinate'
      },
      {
        id: 'alg-7',
        statement: 'Solve the inequality: 3x - 7 < 2x + 5',
        query: 'solve 3x - 7 < 2x + 5',
        difficulty: 'Intermediate',
        hint: 'Collect x terms on one side and constants on the other'
      },
      {
        id: 'alg-8',
        statement: 'Simplify the rational expression: (x² - 9)/(x² + 6x + 9)',
        query: 'simplify (x^2 - 9)/(x^2 + 6x + 9)',
        difficulty: 'Advanced',
        hint: 'Factor both numerator and denominator first'
      },
      {
        id: 'alg-9',
        statement: 'Solve the exponential equation: 2^(x+1) = 32',
        query: 'solve 2^(x+1) = 32',
        difficulty: 'Advanced',
        hint: 'Express 32 as a power of 2'
      },
      {
        id: 'alg-10',
        statement: 'Find all solutions to: x⁴ - 5x² + 4 = 0',
        query: 'solve x^4 - 5x^2 + 4 = 0',
        difficulty: 'Advanced',
        hint: 'Use substitution: let u = x²'
      },
      {
        id: 'alg-11',
        statement: 'Solve the absolute value equation: |2x - 3| = 7',
        query: 'solve |2x - 3| = 7',
        difficulty: 'Advanced',
        hint: 'Consider both positive and negative cases'
      },
      {
        id: 'alg-12',
        statement: 'Find the inverse function of f(x) = 3x - 5',
        query: 'inverse of f(x) = 3x - 5',
        difficulty: 'Advanced',
        hint: 'Replace f(x) with y, swap x and y, then solve for y'
      }
    ]
  },
  {
    id: 'calculus',
    name: 'Calculus',
    icon: 'ri-line-chart-line',
    color: 'from-blue-500 to-indigo-600',
    problemCount: 12,
    problems: [
      {
        id: 'calc-1',
        statement: 'Find the derivative of f(x) = x³',
        query: 'derivative of x^3',
        difficulty: 'Beginner',
        hint: 'Use the power rule: d/dx[xⁿ] = n·xⁿ⁻¹'
      },
      {
        id: 'calc-2',
        statement: 'Evaluate the limit: lim(x→2) of (x² - 4)/(x - 2)',
        query: 'limit (x^2 - 4)/(x - 2) as x approaches 2',
        difficulty: 'Beginner',
        hint: 'Factor the numerator first'
      },
      {
        id: 'calc-3',
        statement: 'Find the integral of f(x) = 2x',
        query: 'integrate 2x dx',
        difficulty: 'Beginner',
        hint: 'Use the power rule for integration'
      },
      {
        id: 'calc-4',
        statement: 'Find the derivative of f(x) = sin(x) · cos(x)',
        query: 'derivative of sin(x)*cos(x)',
        difficulty: 'Intermediate',
        hint: 'Use the product rule: (uv)\' = u\'v + uv\''
      },
      {
        id: 'calc-5',
        statement: 'Evaluate the definite integral: ∫₀² (x² + 1) dx',
        query: 'integrate x^2 + 1 from 0 to 2',
        difficulty: 'Intermediate',
        hint: 'Find the antiderivative first, then apply the limits'
      },
      {
        id: 'calc-6',
        statement: 'Find the critical points of f(x) = x³ - 3x² + 2',
        query: 'critical points of x^3 - 3x^2 + 2',
        difficulty: 'Intermediate',
        hint: 'Set the first derivative equal to zero'
      },
      {
        id: 'calc-7',
        statement: 'Find the derivative of f(x) = ln(x² + 1)',
        query: 'derivative of ln(x^2 + 1)',
        difficulty: 'Intermediate',
        hint: 'Use the chain rule'
      },
      {
        id: 'calc-8',
        statement: 'Evaluate the integral: ∫ x·eˣ dx',
        query: 'integrate x*e^x dx',
        difficulty: 'Advanced',
        hint: 'Use integration by parts: ∫u dv = uv - ∫v du'
      },
      {
        id: 'calc-9',
        statement: 'Find the second derivative of f(x) = x⁴ - 2x³ + x',
        query: 'second derivative of x^4 - 2x^3 + x',
        difficulty: 'Advanced',
        hint: 'Take the derivative twice'
      },
      {
        id: 'calc-10',
        statement: 'Evaluate: ∫ 1/(x² + 1) dx',
        query: 'integrate 1/(x^2 + 1) dx',
        difficulty: 'Advanced',
        hint: 'This is the derivative of arctan(x)'
      },
      {
        id: 'calc-11',
        statement: 'Find the Taylor series of eˣ centered at x = 0',
        query: 'Taylor series of e^x at x=0',
        difficulty: 'Advanced',
        hint: 'Calculate successive derivatives and evaluate at x = 0'
      },
      {
        id: 'calc-12',
        statement: 'Evaluate the improper integral: ∫₁^∞ 1/x² dx',
        query: 'integrate 1/x^2 from 1 to infinity',
        difficulty: 'Advanced',
        hint: 'Use a limit as the upper bound approaches infinity'
      }
    ]
  },
  {
    id: 'statistics',
    name: 'Statistics',
    icon: 'ri-bar-chart-box-line',
    color: 'from-purple-500 to-pink-600',
    problemCount: 10,
    problems: [
      {
        id: 'stat-1',
        statement: 'Find the mean of the dataset: 4, 8, 6, 5, 3, 7',
        query: 'mean of {4, 8, 6, 5, 3, 7}',
        difficulty: 'Beginner',
        hint: 'Add all values and divide by the count'
      },
      {
        id: 'stat-2',
        statement: 'Find the median of: 12, 7, 3, 15, 9, 11',
        query: 'median of {12, 7, 3, 15, 9, 11}',
        difficulty: 'Beginner',
        hint: 'Sort the data first, then find the middle value'
      },
      {
        id: 'stat-3',
        statement: 'Calculate the standard deviation of: 2, 4, 4, 4, 5, 5, 7, 9',
        query: 'standard deviation of {2, 4, 4, 4, 5, 5, 7, 9}',
        difficulty: 'Beginner',
        hint: 'Find the variance first, then take the square root'
      },
      {
        id: 'stat-4',
        statement: 'Find the probability of rolling a sum of 7 with two dice',
        query: 'probability of sum 7 with 2 dice',
        difficulty: 'Intermediate',
        hint: 'Count favorable outcomes and divide by total outcomes (36)'
      },
      {
        id: 'stat-5',
        statement: 'Calculate the correlation coefficient for x = {1,2,3,4,5} and y = {2,4,5,4,5}',
        query: 'correlation of {1,2,3,4,5} and {2,4,5,4,5}',
        difficulty: 'Intermediate',
        hint: 'Use Pearson correlation formula'
      },
      {
        id: 'stat-6',
        statement: 'Find P(X ≤ 2) for a binomial distribution with n=5, p=0.3',
        query: 'binomial probability n=5 p=0.3 x<=2',
        difficulty: 'Intermediate',
        hint: 'Sum probabilities for x = 0, 1, and 2'
      },
      {
        id: 'stat-7',
        statement: 'Calculate the 95% confidence interval for mean with sample mean=50, std=10, n=25',
        query: '95% confidence interval mean=50 std=10 n=25',
        difficulty: 'Advanced',
        hint: 'Use z-score of 1.96 for 95% confidence'
      },
      {
        id: 'stat-8',
        statement: 'Perform a chi-square test for independence on a 2×2 contingency table',
        query: 'chi-square test {{10,20},{15,25}}',
        difficulty: 'Advanced',
        hint: 'Calculate expected frequencies and compare with observed'
      },
      {
        id: 'stat-9',
        statement: 'Find the z-score for x=85 in a distribution with mean=75 and std=8',
        query: 'z-score x=85 mean=75 std=8',
        difficulty: 'Intermediate',
        hint: 'Use formula: z = (x - μ) / σ'
      },
      {
        id: 'stat-10',
        statement: 'Calculate the variance of: 3, 7, 7, 19, 24',
        query: 'variance of {3, 7, 7, 19, 24}',
        difficulty: 'Beginner',
        hint: 'Find the mean first, then calculate squared deviations'
      }
    ]
  },
  {
    id: 'physics',
    name: 'Physics',
    icon: 'ri-flashlight-line',
    color: 'from-orange-500 to-red-600',
    problemCount: 10,
    problems: [
      {
        id: 'phys-1',
        statement: 'Calculate velocity: distance = 100m, time = 5s',
        query: 'velocity = 100 meters / 5 seconds',
        difficulty: 'Beginner',
        hint: 'Use v = d/t'
      },
      {
        id: 'phys-2',
        statement: 'Find kinetic energy: mass = 10kg, velocity = 5m/s',
        query: 'kinetic energy mass=10kg velocity=5m/s',
        difficulty: 'Beginner',
        hint: 'Use KE = ½mv²'
      },
      {
        id: 'phys-3',
        statement: 'Calculate force: mass = 20kg, acceleration = 3m/s²',
        query: 'force = 20 kg * 3 m/s^2',
        difficulty: 'Beginner',
        hint: 'Use Newton\'s second law: F = ma'
      },
      {
        id: 'phys-4',
        statement: 'Find gravitational potential energy: mass = 5kg, height = 10m (g = 9.8m/s²)',
        query: 'potential energy mass=5kg height=10m g=9.8m/s^2',
        difficulty: 'Intermediate',
        hint: 'Use PE = mgh'
      },
      {
        id: 'phys-5',
        statement: 'Calculate momentum: mass = 15kg, velocity = 8m/s',
        query: 'momentum = 15 kg * 8 m/s',
        difficulty: 'Intermediate',
        hint: 'Use p = mv'
      },
      {
        id: 'phys-6',
        statement: 'Find the period of a pendulum with length = 2m (g = 9.8m/s²)',
        query: 'pendulum period length=2m g=9.8m/s^2',
        difficulty: 'Intermediate',
        hint: 'Use T = 2π√(L/g)'
      },
      {
        id: 'phys-7',
        statement: 'Calculate work done: force = 50N, distance = 10m, angle = 30°',
        query: 'work = 50 N * 10 m * cos(30 degrees)',
        difficulty: 'Advanced',
        hint: 'Use W = F·d·cos(θ)'
      },
      {
        id: 'phys-8',
        statement: 'Find the escape velocity from Earth (mass = 5.97×10²⁴kg, radius = 6.37×10⁶m)',
        query: 'escape velocity Earth mass=5.97e24kg radius=6.37e6m',
        difficulty: 'Advanced',
        hint: 'Use v = √(2GM/r)'
      },
      {
        id: 'phys-9',
        statement: 'Calculate electric field: charge = 5×10⁻⁶C, distance = 0.1m',
        query: 'electric field charge=5e-6C distance=0.1m',
        difficulty: 'Advanced',
        hint: 'Use E = kQ/r² where k = 8.99×10⁹'
      },
      {
        id: 'phys-10',
        statement: 'Find the wavelength of light with frequency = 5×10¹⁴Hz (c = 3×10⁸m/s)',
        query: 'wavelength = 3e8 m/s / 5e14 Hz',
        difficulty: 'Intermediate',
        hint: 'Use λ = c/f'
      }
    ]
  },
  {
    id: 'engineering',
    name: 'Engineering',
    icon: 'ri-tools-line',
    color: 'from-slate-500 to-gray-700',
    problemCount: 10,
    problems: [
      {
        id: 'eng-1',
        statement: 'Calculate stress: force = 1000N, area = 0.01m²',
        query: 'stress = 1000 N / 0.01 m^2',
        difficulty: 'Beginner',
        hint: 'Use σ = F/A'
      },
      {
        id: 'eng-2',
        statement: 'Find strain: change in length = 0.5mm, original length = 100mm',
        query: 'strain = 0.5 mm / 100 mm',
        difficulty: 'Beginner',
        hint: 'Use ε = ΔL/L₀'
      },
      {
        id: 'eng-3',
        statement: 'Calculate power: voltage = 12V, current = 5A',
        query: 'power = 12 V * 5 A',
        difficulty: 'Beginner',
        hint: 'Use P = VI'
      },
      {
        id: 'eng-4',
        statement: 'Find resistance in series: R1 = 10Ω, R2 = 15Ω, R3 = 20Ω',
        query: 'total resistance = 10 ohm + 15 ohm + 20 ohm',
        difficulty: 'Intermediate',
        hint: 'In series: R_total = R1 + R2 + R3'
      },
      {
        id: 'eng-5',
        statement: 'Calculate torque: force = 50N, distance = 0.3m',
        query: 'torque = 50 N * 0.3 m',
        difficulty: 'Intermediate',
        hint: 'Use τ = F × r'
      },
      {
        id: 'eng-6',
        statement: 'Find capacitance in parallel: C1 = 10μF, C2 = 20μF',
        query: 'capacitance parallel = 10 microfarad + 20 microfarad',
        difficulty: 'Intermediate',
        hint: 'In parallel: C_total = C1 + C2'
      },
      {
        id: 'eng-7',
        statement: 'Calculate Young\'s modulus: stress = 200MPa, strain = 0.001',
        query: 'Young modulus = 200 MPa / 0.001',
        difficulty: 'Advanced',
        hint: 'Use E = σ/ε'
      },
      {
        id: 'eng-8',
        statement: 'Find the moment of inertia for a solid cylinder: mass = 5kg, radius = 0.2m',
        query: 'moment of inertia cylinder mass=5kg radius=0.2m',
        difficulty: 'Advanced',
        hint: 'Use I = ½mr² for solid cylinder'
      },
      {
        id: 'eng-9',
        statement: 'Calculate heat transfer rate: k = 50W/mK, A = 2m², ΔT = 30K, thickness = 0.1m',
        query: 'heat transfer = 50 * 2 * 30 / 0.1',
        difficulty: 'Advanced',
        hint: 'Use Q = kAΔT/d'
      },
      {
        id: 'eng-10',
        statement: 'Find resonant frequency: inductance = 0.1H, capacitance = 100μF',
        query: 'resonant frequency L=0.1H C=100e-6F',
        difficulty: 'Advanced',
        hint: 'Use f = 1/(2π√(LC))'
      }
    ]
  },
  {
    id: 'computational',
    name: 'Computational Sciences',
    icon: 'ri-cpu-line',
    color: 'from-cyan-500 to-blue-600',
    problemCount: 10,
    problems: [
      {
        id: 'comp-1',
        statement: 'Convert binary 1011 to decimal',
        query: 'binary 1011 to decimal',
        difficulty: 'Beginner',
        hint: 'Multiply each digit by powers of 2'
      },
      {
        id: 'comp-2',
        statement: 'Convert decimal 25 to binary',
        query: 'decimal 25 to binary',
        difficulty: 'Beginner',
        hint: 'Divide by 2 repeatedly and track remainders'
      },
      {
        id: 'comp-3',
        statement: 'Calculate 2⁸',
        query: '2^8',
        difficulty: 'Beginner',
        hint: 'Multiply 2 by itself 8 times'
      },
      {
        id: 'comp-4',
        statement: 'Find the greatest common divisor of 48 and 18',
        query: 'gcd(48, 18)',
        difficulty: 'Intermediate',
        hint: 'Use the Euclidean algorithm'
      },
      {
        id: 'comp-5',
        statement: 'Calculate the factorial of 7',
        query: '7!',
        difficulty: 'Intermediate',
        hint: 'Multiply 7 × 6 × 5 × 4 × 3 × 2 × 1'
      },
      {
        id: 'comp-6',
        statement: 'Find the number of permutations of 5 items taken 3 at a time',
        query: 'permutations(5, 3)',
        difficulty: 'Intermediate',
        hint: 'Use P(n,r) = n!/(n-r)!'
      },
      {
        id: 'comp-7',
        statement: 'Calculate the Fibonacci number at position 10',
        query: 'fibonacci(10)',
        difficulty: 'Advanced',
        hint: 'Each number is the sum of the previous two'
      },
      {
        id: 'comp-8',
        statement: 'Find the determinant of matrix [[3,1],[2,4]]',
        query: 'determinant {{3,1},{2,4}}',
        difficulty: 'Advanced',
        hint: 'For 2×2: ad - bc'
      },
      {
        id: 'comp-9',
        statement: 'Calculate the eigenvalues of matrix [[4,1],[2,3]]',
        query: 'eigenvalues {{4,1},{2,3}}',
        difficulty: 'Advanced',
        hint: 'Solve det(A - λI) = 0'
      },
      {
        id: 'comp-10',
        statement: 'Find the inverse of matrix [[2,1],[5,3]]',
        query: 'inverse {{2,1},{5,3}}',
        difficulty: 'Intermediate',
        hint: 'Use the formula A⁻¹ = (1/det(A)) × adj(A)'
      }
    ]
  },
  {
    id: 'earth-sciences',
    name: 'Earth Sciences',
    icon: 'ri-earth-line',
    color: 'from-green-500 to-emerald-600',
    problemCount: 8,
    problems: [
      {
        id: 'earth-1',
        statement: 'Calculate the circumference of Earth (radius = 6371 km)',
        query: 'circumference = 2 * pi * 6371 km',
        difficulty: 'Beginner',
        hint: 'Use C = 2πr'
      },
      {
        id: 'earth-2',
        statement: 'Find the volume of Earth (radius = 6371 km)',
        query: 'volume sphere radius=6371 km',
        difficulty: 'Beginner',
        hint: 'Use V = (4/3)πr³'
      },
      {
        id: 'earth-3',
        statement: 'Calculate atmospheric pressure at sea level (101325 Pa) in atm',
        query: '101325 Pa to atm',
        difficulty: 'Beginner',
        hint: '1 atm = 101325 Pa'
      },
      {
        id: 'earth-4',
        statement: 'Find the distance traveled by a seismic wave: velocity = 8 km/s, time = 5 minutes',
        query: 'distance = 8 km/s * 5 minutes',
        difficulty: 'Intermediate',
        hint: 'Convert time to seconds first'
      },
      {
        id: 'earth-5',
        statement: 'Calculate the Richter magnitude difference between earthquakes of 6.0 and 7.0',
        query: '10^(7.0 - 6.0)',
        difficulty: 'Intermediate',
        hint: 'Each whole number increase is 10× amplitude'
      },
      {
        id: 'earth-6',
        statement: 'Find the density of Earth: mass = 5.97×10²⁴kg, volume = 1.08×10²¹m³',
        query: 'density = 5.97e24 kg / 1.08e21 m^3',
        difficulty: 'Intermediate',
        hint: 'Use ρ = m/V'
      },
      {
        id: 'earth-7',
        statement: 'Calculate the solar constant at Earth\'s orbit (power = 3.828×10²⁶W, distance = 1.496×10¹¹m)',
        query: 'solar constant = 3.828e26 W / (4 * pi * (1.496e11 m)^2)',
        difficulty: 'Advanced',
        hint: 'Divide total power by sphere surface area'
      },
      {
        id: 'earth-8',
        statement: 'Find the orbital velocity of Earth (radius = 1.496×10⁸km, period = 365.25 days)',
        query: 'orbital velocity = 2 * pi * 1.496e8 km / 365.25 days',
        difficulty: 'Advanced',
        hint: 'Use v = 2πr/T'
      }
    ]
  },
  {
    id: 'differential-equations',
    name: 'Differential Equations',
    icon: 'ri-git-branch-line',
    color: 'from-rose-500 to-pink-600',
    problemCount: 10,
    problems: [
      {
        id: 'diff-1',
        statement: 'Solve the differential equation: dy/dx = 2x',
        query: 'solve dy/dx = 2x',
        difficulty: 'Beginner',
        hint: 'Integrate both sides'
      },
      {
        id: 'diff-2',
        statement: 'Solve: dy/dx = 3y',
        query: 'solve dy/dx = 3y',
        difficulty: 'Beginner',
        hint: 'This is a separable equation'
      },
      {
        id: 'diff-3',
        statement: 'Solve: dy/dx = x + y',
        query: 'solve dy/dx = x + y',
        difficulty: 'Intermediate',
        hint: 'Try an integrating factor or substitution'
      },
      {
        id: 'diff-4',
        statement: 'Solve the initial value problem: dy/dx = 2x, y(0) = 1',
        query: 'solve dy/dx = 2x, y(0) = 1',
        difficulty: 'Intermediate',
        hint: 'Find the general solution first, then apply initial condition'
      },
      {
        id: 'diff-5',
        statement: 'Solve: d²y/dx² + 4y = 0',
        query: 'solve d^2y/dx^2 + 4y = 0',
        difficulty: 'Intermediate',
        hint: 'This is a second-order linear homogeneous equation'
      },
      {
        id: 'diff-6',
        statement: 'Solve: dy/dx = (x + y)/(x - y)',
        query: 'solve dy/dx = (x + y)/(x - y)',
        difficulty: 'Advanced',
        hint: 'Try a substitution like v = y/x'
      },
      {
        id: 'diff-7',
        statement: 'Solve: d²y/dx² - 3dy/dx + 2y = 0',
        query: 'solve d^2y/dx^2 - 3dy/dx + 2y = 0',
        difficulty: 'Advanced',
        hint: 'Find the characteristic equation'
      },
      {
        id: 'diff-8',
        statement: 'Solve: dy/dx + y/x = x²',
        query: 'solve dy/dx + y/x = x^2',
        difficulty: 'Advanced',
        hint: 'This is a first-order linear equation, use integrating factor'
      },
      {
        id: 'diff-9',
        statement: 'Solve: x dy/dx = y + x²',
        query: 'solve x dy/dx = y + x^2',
        difficulty: 'Intermediate',
        hint: 'Rewrite in standard form first'
      },
      {
        id: 'diff-10',
        statement: 'Solve the system: dx/dt = 2x + y, dy/dt = x + 2y',
        query: 'solve dx/dt = 2x + y, dy/dt = x + 2y',
        difficulty: 'Advanced',
        hint: 'Use matrix methods or elimination'
      }
    ]
  },
  {
    id: 'geometry',
    name: 'Geometry',
    icon: 'ri-shape-line',
    color: 'from-amber-500 to-orange-600',
    problemCount: 10,
    problems: [
      {
        id: 'geo-1',
        statement: 'Find the area of a circle with radius = 5',
        query: 'area circle radius=5',
        difficulty: 'Beginner',
        hint: 'Use A = πr²'
      },
      {
        id: 'geo-2',
        statement: 'Calculate the perimeter of a rectangle: length = 8, width = 5',
        query: 'perimeter rectangle length=8 width=5',
        difficulty: 'Beginner',
        hint: 'Use P = 2(l + w)'
      },
      {
        id: 'geo-3',
        statement: 'Find the volume of a cube with side = 4',
        query: 'volume cube side=4',
        difficulty: 'Beginner',
        hint: 'Use V = s³'
      },
      {
        id: 'geo-4',
        statement: 'Calculate the area of a triangle: base = 10, height = 6',
        query: 'area triangle base=10 height=6',
        difficulty: 'Beginner',
        hint: 'Use A = ½bh'
      },
      {
        id: 'geo-5',
        statement: 'Find the surface area of a sphere with radius = 3',
        query: 'surface area sphere radius=3',
        difficulty: 'Intermediate',
        hint: 'Use A = 4πr²'
      },
      {
        id: 'geo-6',
        statement: 'Calculate the volume of a cylinder: radius = 4, height = 10',
        query: 'volume cylinder radius=4 height=10',
        difficulty: 'Intermediate',
        hint: 'Use V = πr²h'
      },
      {
        id: 'geo-7',
        statement: 'Find the distance between points (3, 4) and (7, 1)',
        query: 'distance between (3,4) and (7,1)',
        difficulty: 'Intermediate',
        hint: 'Use the distance formula: √[(x₂-x₁)² + (y₂-y₁)²]'
      },
      {
        id: 'geo-8',
        statement: 'Calculate the volume of a cone: radius = 5, height = 12',
        query: 'volume cone radius=5 height=12',
        difficulty: 'Advanced',
        hint: 'Use V = (1/3)πr²h'
      },
      {
        id: 'geo-9',
        statement: 'Find the area of a regular hexagon with side = 6',
        query: 'area regular hexagon side=6',
        difficulty: 'Advanced',
        hint: 'Use A = (3√3/2)s²'
      },
      {
        id: 'geo-10',
        statement: 'Calculate the surface area of a cylinder: radius = 3, height = 8',
        query: 'surface area cylinder radius=3 height=8',
        difficulty: 'Advanced',
        hint: 'Use A = 2πr² + 2πrh'
      }
    ]
  },
  {
    id: 'chemistry',
    name: 'Chemistry',
    icon: 'ri-test-tube-line',
    color: 'from-teal-500 to-cyan-600',
    problemCount: 10,
    problems: [
      {
        id: 'chem-1',
        statement: 'Calculate the molar mass of H₂O',
        query: 'molar mass H2O',
        difficulty: 'Beginner',
        hint: 'Add atomic masses: H(1) × 2 + O(16)'
      },
      {
        id: 'chem-2',
        statement: 'Find the number of moles in 36g of H₂O',
        query: 'moles in 36 grams H2O',
        difficulty: 'Beginner',
        hint: 'Use n = mass / molar mass'
      },
      {
        id: 'chem-3',
        statement: 'Calculate the pH of a solution with [H⁺] = 1×10⁻⁵ M',
        query: 'pH = -log(1e-5)',
        difficulty: 'Beginner',
        hint: 'Use pH = -log[H⁺]'
      },
      {
        id: 'chem-4',
        statement: 'Balance the equation: H₂ + O₂ → H₂O',
        query: 'balance H2 + O2 -> H2O',
        difficulty: 'Intermediate',
        hint: 'Make sure atoms are equal on both sides'
      },
      {
        id: 'chem-5',
        statement: 'Calculate the molarity of 2 moles of NaCl in 500 mL solution',
        query: 'molarity = 2 moles / 0.5 liters',
        difficulty: 'Intermediate',
        hint: 'Use M = moles / liters'
      },
      {
        id: 'chem-6',
        statement: 'Find the mass of 0.5 moles of CO₂',
        query: 'mass of 0.5 moles CO2',
        difficulty: 'Intermediate',
        hint: 'Use mass = moles × molar mass'
      },
      {
        id: 'chem-7',
        statement: 'Calculate the energy of a photon with wavelength = 500 nm (h = 6.626×10⁻³⁴ J·s, c = 3×10⁸ m/s)',
        query: 'energy = 6.626e-34 * 3e8 / 500e-9',
        difficulty: 'Advanced',
        hint: 'Use E = hc/λ'
      },
      {
        id: 'chem-8',
        statement: 'Find the equilibrium constant expression for: 2A + B ⇌ C',
        query: 'equilibrium constant 2A + B <-> C',
        difficulty: 'Advanced',
        hint: 'K = [C] / ([A]²[B])'
      },
      {
        id: 'chem-9',
        statement: 'Calculate the half-life of a first-order reaction with k = 0.0693 s⁻¹',
        query: 'half-life = ln(2) / 0.0693',
        difficulty: 'Advanced',
        hint: 'Use t₁/₂ = ln(2)/k'
      },
      {
        id: 'chem-10',
        statement: 'Find the oxidation state of Mn in KMnO₄',
        query: 'oxidation state Mn in KMnO4',
        difficulty: 'Intermediate',
        hint: 'K is +1, O is -2, sum must equal 0'
      }
    ]
  },
  {
    id: 'number-theory',
    name: 'Number Theory',
    icon: 'ri-hashtag',
    color: 'from-violet-500 to-purple-600',
    problemCount: 10,
    problems: [
      {
        id: 'num-1',
        statement: 'Find all prime numbers less than 20',
        query: 'prime numbers less than 20',
        difficulty: 'Beginner',
        hint: 'A prime has exactly two divisors: 1 and itself'
      },
      {
        id: 'num-2',
        statement: 'Check if 17 is prime',
        query: 'is 17 prime',
        difficulty: 'Beginner',
        hint: 'Test divisibility by primes up to √17'
      },
      {
        id: 'num-3',
        statement: 'Find the greatest common divisor of 24 and 36',
        query: 'gcd(24, 36)',
        difficulty: 'Beginner',
        hint: 'Use the Euclidean algorithm'
      },
      {
        id: 'num-4',
        statement: 'Find the least common multiple of 12 and 18',
        query: 'lcm(12, 18)',
        difficulty: 'Intermediate',
        hint: 'Use lcm(a,b) = (a×b)/gcd(a,b)'
      },
      {
        id: 'num-5',
        statement: 'Find the prime factorization of 60',
        query: 'prime factorization 60',
        difficulty: 'Intermediate',
        hint: 'Divide by smallest primes repeatedly'
      },
      {
        id: 'num-6',
        statement: 'Calculate 7 mod 3',
        query: '7 mod 3',
        difficulty: 'Beginner',
        hint: 'Find the remainder when 7 is divided by 3'
      },
      {
        id: 'num-7',
        statement: 'Find the number of divisors of 36',
        query: 'number of divisors 36',
        difficulty: 'Intermediate',
        hint: 'Use prime factorization: 36 = 2² × 3²'
      },
      {
        id: 'num-8',
        statement: 'Calculate Euler\'s totient function φ(12)',
        query: 'euler totient 12',
        difficulty: 'Advanced',
        hint: 'Count integers ≤ 12 that are coprime to 12'
      },
      {
        id: 'num-9',
        statement: 'Find the modular inverse of 3 modulo 7',
        query: 'modular inverse 3 mod 7',
        difficulty: 'Advanced',
        hint: 'Find x such that 3x ≡ 1 (mod 7)'
      },
      {
        id: 'num-10',
        statement: 'Solve the congruence: 5x ≡ 3 (mod 7)',
        query: 'solve 5x = 3 mod 7',
        difficulty: 'Advanced',
        hint: 'Multiply both sides by the modular inverse of 5'
      }
    ]
  },
  {
    id: 'linear-algebra',
    name: 'Linear Algebra',
    icon: 'ri-grid-line',
    color: 'from-indigo-500 to-blue-600',
    problemCount: 10,
    problems: [
      {
        id: 'lin-1',
        statement: 'Add matrices: [[1,2],[3,4]] + [[5,6],[7,8]]',
        query: '{{1,2},{3,4}} + {{5,6},{7,8}}',
        difficulty: 'Beginner',
        hint: 'Add corresponding elements'
      },
      {
        id: 'lin-2',
        statement: 'Multiply matrix by scalar: 3 × [[2,1],[0,4]]',
        query: '3 * {{2,1},{0,4}}',
        difficulty: 'Beginner',
        hint: 'Multiply each element by 3'
      },
      {
        id: 'lin-3',
        statement: 'Find the transpose of [[1,2,3],[4,5,6]]',
        query: 'transpose {{1,2,3},{4,5,6}}',
        difficulty: 'Beginner',
        hint: 'Swap rows and columns'
      },
      {
        id: 'lin-4',
        statement: 'Multiply matrices: [[1,2],[3,4]] × [[5,6],[7,8]]',
        query: '{{1,2},{3,4}} * {{5,6},{7,8}}',
        difficulty: 'Intermediate',
        hint: 'Use row-by-column multiplication'
      },
      {
        id: 'lin-5',
        statement: 'Find the determinant of [[2,3],[1,4]]',
        query: 'determinant {{2,3},{1,4}}',
        difficulty: 'Intermediate',
        hint: 'For 2×2: ad - bc'
      },
      {
        id: 'lin-6',
        statement: 'Find the inverse of [[1,2],[3,4]]',
        query: 'inverse {{1,2},{3,4}}',
        difficulty: 'Intermediate',
        hint: 'Use A⁻¹ = (1/det(A)) × adj(A)'
      },
      {
        id: 'lin-7',
        statement: 'Find the eigenvalues of [[3,1],[0,2]]',
        query: 'eigenvalues {{3,1},{0,2}}',
        difficulty: 'Advanced',
        hint: 'Solve det(A - λI) = 0'
      },
      {
        id: 'lin-8',
        statement: 'Find the rank of [[1,2,3],[2,4,6],[1,1,1]]',
        query: 'rank {{1,2,3},{2,4,6},{1,1,1}}',
        difficulty: 'Advanced',
        hint: 'Count linearly independent rows'
      },
      {
        id: 'lin-9',
        statement: 'Calculate the dot product of vectors [1,2,3] and [4,5,6]',
        query: 'dot product {1,2,3} and {4,5,6}',
        difficulty: 'Intermediate',
        hint: 'Multiply corresponding components and sum'
      },
      {
        id: 'lin-10',
        statement: 'Find the cross product of [1,0,0] and [0,1,0]',
        query: 'cross product {1,0,0} and {0,1,0}',
        difficulty: 'Advanced',
        hint: 'Use the determinant formula with i, j, k'
      }
    ]
  }
];